import './init.ts';

import manropeWoff2 from '@fontsource-variable/manrope/files/manrope-latin-wght-normal.woff2?url';
import manropeCss from '@fontsource-variable/manrope/wght.css?inline';
import { CssBaseline, ThemeProvider } from '@mui/material';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { LazyMotion } from 'motion/react';
import { useEffect } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { API } from 'shared/config';

import type { Route } from './+types/root.ts';
import rootStyles from './assets/styles/index.css?inline';
import theme from './assets/styles/theme.js';
import { Analytics } from './components/Analytics/index.ts';
import { NProgress } from './components/NProgress/index.ts';
import { AutoTracking } from './lib/analytics/auto-tracking.tsx';
import { initializeGTM } from './lib/analytics/gtm.ts';
import { configureAnalyticsBatching } from './lib/analytics/unified.ts';
import { authInitQuery } from './lib/api/queries.ts';
import { flagsQuery } from './lib/flags.ts';
import { InAppGateProvider } from './lib/in-app-gate.tsx';
import { ServerNowContext } from './lib/now.ts';
import { getQueryClient, queryClientContext } from './lib/query-client-context.ts';
import { buildQueryClient } from './lib/query-client.server.ts';
import { queryClient } from './queryClient.ts';
import { RootRouteErrorBoundary } from './route-error-boundary.tsx';
import { AuthProvider } from './services/Auth/index.ts';
import { BrainSessionProvider } from './services/BrainSession/BrainSession.tsx';

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="no">
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:image" content={`${API.domain}/og-default.png`} />
        <Meta />

        {/* Inlining styles is faster than regular loading, these styles are used everywhere so it's not a problem */}
        {/* When not using preloading styles would be loaded after all of the js bundles which are not critical for the first paint, inlining is faster and easier to set up than preloading */}
        <style dangerouslySetInnerHTML={{ __html: rootStyles }} suppressHydrationWarning />
        <style dangerouslySetInnerHTML={{ __html: manropeCss }} />
        <link
          rel="preload"
          href={manropeWoff2}
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
        />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

/**
 * Every loader reads its query client from the context instead of importing one, so the same
 * data helpers can serve the build and the browser. The build client lives for the whole
 * prerender, which is what keeps shared data to a single request per build.
 */
export const middleware: Route.MiddlewareFunction[] = [
  ({ context }, next) => {
    context.set(queryClientContext, buildQueryClient);
    return next();
  },
];

export const clientMiddleware: Route.ClientMiddlewareFunction[] = [
  ({ context }, next) => {
    context.set(queryClientContext, queryClient);
    return next();
  },
];

/**
 * Runs during prerendering (and for the SPA fallback), so flag-dependent chrome like the
 * navigation renders in the static HTML instead of shifting after a client-side fetch.
 * `loader` on the root route is the only server loader allowed with `ssr: false`.
 */
export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const flags = await queryClient.query(flagsQuery());
  return { flags, serverNow: Date.now() };
}

export const HydrateFallback = () => {
  return (
    <div className="full-page-loader">
      <img width="200" src="/logo.png" alt="Scoopr logo" />
    </div>
  );
};

/** Framework root route error boundary (replaces legacy `routing.tsx`). */
export const ErrorBoundary = RootRouteErrorBoundary;

const loadFeatures = () => import('./framer-features.ts').then((res) => res.default);

export default function Root({ loaderData }: Route.ComponentProps) {
  // Configured during render because child effects (which send events) run before parent effects
  if (!import.meta.env.SSR) {
    configureAnalyticsBatching(loaderData.flags.analyticsBatching);
  }

  useEffect(() => {
    initializeGTM();
  }, []);

  return (
    <ServerNowContext.Provider value={loaderData.serverNow}>
      <Sentry.ErrorBoundary
        beforeCapture={(scope) => {
          scope.setLevel('fatal');
          scope.setTag('app-crash', true);
        }}
      >
        <AutoTracking>
          <ThemeProvider theme={theme}>
            <QueryClientProvider client={queryClient}>
              <InAppGateProvider>
                <AuthProvider>
                  <BrainSessionProvider>
                    <NProgress />
                    <CssBaseline />
                    <Analytics />
                    <LazyMotion features={loadFeatures} strict>
                      <Outlet />
                    </LazyMotion>
                  </BrainSessionProvider>
                </AuthProvider>
              </InAppGateProvider>
            </QueryClientProvider>
          </ThemeProvider>
        </AutoTracking>
      </Sentry.ErrorBoundary>
    </ServerNowContext.Provider>
  );
}

if (!import.meta.env.SSR) {
  void queryClient.query(authInitQuery()).catch(() => {});
}
